import { useEffect, useState, useCallback } from "react";
import { useAccount, useDisconnect } from "wagmi";
import { useWallet } from "@solana/wallet-adapter-react";
import { useAuth } from "./useAuth";

interface WalletSeparationState {
  evmConnected: boolean;
  solanaConnected: boolean;
  evmAddress: string | null;
  solanaAddress: string | null;
  isAuth: boolean;
  shouldMaintainEvmConnection: boolean;
}

export const useWalletSeparation = () => {
  const { address: evmAddress, isConnected: evmConnected } = useAccount();
  const {
    publicKey: solanaPublicKey,
    connected: solanaConnected,
    disconnect: disconnectSolana,
  } = useWallet();
  const { disconnect: disconnectEvm } = useDisconnect();
  const isAuth = useAuth();

  const [shouldMaintainEvmConnection, setShouldMaintainEvmConnection] =
    useState(true);

  const state: WalletSeparationState = {
    evmConnected,
    solanaConnected,
    evmAddress: evmAddress || null,
    solanaAddress: solanaPublicKey?.toString() || null,
    isAuth,
    shouldMaintainEvmConnection,
  };

  // Ensure EVM connection is maintained when Solana connects
  useEffect(() => {
    if (solanaConnected && !evmConnected && shouldMaintainEvmConnection) {
      console.log(
        "Solana connected but EVM disconnected - maintaining EVM as primary connection"
      );
      // If EVM was disconnected when Solana connected, we should remind user to reconnect EVM
    }
  }, [solanaConnected, evmConnected, shouldMaintainEvmConnection]);

  // Handle proper disconnection logic
  const handleSolanaDisconnect = useCallback(() => {
    disconnectSolana();
    // Don't disconnect EVM - keep it as the main connection
    console.log("Solana wallet disconnected, EVM connection maintained");
  }, [disconnectSolana]);

  const handleEvmDisconnect = useCallback(() => {
    disconnectEvm();
    // Also disconnect Solana if it's connected, as it depends on EVM for auth
    if (solanaConnected) {
      disconnectSolana();
    }
    console.log("EVM wallet disconnected, Solana also disconnected");
  }, [disconnectEvm, disconnectSolana, solanaConnected]);

  const handleFullDisconnect = useCallback(() => {
    if (evmConnected) {
      disconnectEvm();
    }
    if (solanaConnected) {
      disconnectSolana();
    }
    console.log("All wallets disconnected");
  }, [evmConnected, solanaConnected, disconnectEvm, disconnectSolana]);

  return {
    state,
    handlers: {
      handleSolanaDisconnect,
      handleEvmDisconnect,
      handleFullDisconnect,
    },
    utils: {
      setShouldMaintainEvmConnection,
    },
  };
};
